import type { FieldSelection, Filter, RelationQuery } from '#layers/data-model/public';
import { isRelationQuery } from '#layers/data-model/public';

// Index signature must be a supertype of all named properties, hence the union.
// In practice: _-prefixed keys are query options, all other keys are nested DeepEntry.
export interface DeepEntry {
	_limit?: number;
	_offset?: number;
	_sort?: string[];
	_filter?: Filter;
	[nestedRelation: string]: DeepEntry | number | string[] | object | undefined;
}

export interface FlattenedFieldSelection {
	fields: string[];
	deep?: Record<string, DeepEntry>;
}

/**
 * Splits an RStore field selection into flat field paths and the expected `deep` query object.
 * Relation filters remain structured here so fetch option construction can normalize operands later.
 */
export function flattenFieldSelection(selection: FieldSelection, prefix?: string): FlattenedFieldSelection {
	const fields: string[] = [];
	let deep: Record<string, DeepEntry> | undefined;

	for (const entry of selection) {
		if (typeof entry === 'string') {
			fields.push(prefix ? `${prefix}.${entry}` : entry);
			continue;
		}

		for (const [relationApiName, nestedValue] of Object.entries(entry)) {
			const nestedSelection = isRelationQuery(nestedValue) ? nestedValue.fields : nestedValue as FieldSelection;
			const query = isRelationQuery(nestedValue) ? nestedValue : undefined;

			const nestedPrefix = prefix ? `${prefix}.${relationApiName}` : relationApiName;
			const nested = flattenFieldSelection(nestedSelection, nestedPrefix);

			fields.push(...nested.fields);

			const deepEntry = buildDeepEntry(query, nested.deep);

			if (deepEntry) {
				if (!deep) deep = {};
				deep[relationApiName] = deepEntry;
			}
		}
	}

	const result: FlattenedFieldSelection = { fields };
	if (deep) result.deep = deep;
	return result;
}

function buildDeepEntry(query: RelationQuery | undefined, nestedDeep?: Record<string, DeepEntry>): DeepEntry | undefined {
	const entry: DeepEntry = {};

	if (query?.limit !== undefined) entry._limit = query.limit;
	if (query?.offset !== undefined) entry._offset = query.offset;
	if (query?.sort !== undefined) entry._sort = query.sort;
	if (query?.filter !== undefined) entry._filter = query.filter;
	if (nestedDeep) Object.assign(entry, nestedDeep);

	return Object.keys(entry).length > 0 ? entry : undefined;
}
